import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { usePositionsStore } from '@/store/positionsStore';
import type { Position } from '@arrowise/shared';
import { PlusIcon, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';

export interface PositionSelectorProps {
    selectedPosition: Position | null;
    onPositionSelect: (position: Position | null) => void;
    teamId?: string | null;
    required?: boolean;
}

export function PositionSelector({
    selectedPosition,
    onPositionSelect,
    teamId,
    required = false
}: PositionSelectorProps) {
    const { positions, isLoading, loadPositions } = usePositionsStore();
    const [dialogOpen, setDialogOpen] = useState(false);

    // Load positions when component mounts or teamId changes
    useEffect(() => {
        loadPositions(teamId || undefined);
    }, [teamId, loadPositions]);

    const handlePositionChange = (positionId: string) => {
        if (positionId === 'none') {
            onPositionSelect(null);
        } else {
            const position = positions.find(p => p.id === positionId);
            onPositionSelect(position || null);
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
                <Label>Position {required && '*'}</Label>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                            <PlusIcon className="size-4 mr-1" />
                            Create Position
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Create New Position</DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                            <p className="text-sm text-muted-foreground mb-4">
                                You need to create a position first before creating a session.
                            </p>
                            <Link to="/app/positions/create">
                                <Button className="w-full">
                                    <Briefcase className="size-4 mr-2" />
                                    Go to Positions
                                </Button>
                            </Link>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            <Select
                value={selectedPosition?.id || 'none'}
                onValueChange={handlePositionChange}
                disabled={isLoading}
            >
                <SelectTrigger className='cursor-pointer'>
                    <SelectValue placeholder="Select a position..." />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="none" className="cursor-pointer">No position selected</SelectItem>
                    {positions.map((position) => (
                        <SelectItem key={position.id} value={position.id} className="cursor-pointer">
                            <div className="flex items-center gap-2">
                                <Briefcase className="size-4" />
                                <div>
                                    <div className="font-medium">{position.name}</div>
                                    {position.description && (
                                        <div className="text-sm text-muted-foreground">
                                            {position.description}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {positions.length === 0 && !isLoading && (
                <p className="text-sm text-muted-foreground">
                    No positions available. Create a position first to associate with this session.
                </p>
            )}
        </div>
    );
}
